import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiEye, FiX, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';

const ALL_STATUSES = ['PROCESSING', 'CONFIRMED', 'SHIPPED', 'DELIVERED', 'CANCELLED'];
const STATUS_BADGE = { DELIVERED:'badge-green', SHIPPED:'badge-blue', PROCESSING:'badge-yellow', CONFIRMED:'badge-blue', CANCELLED:'badge-pink', RETURNED:'badge-gray' };
const PER_PAGE = 15;

function fmtDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/orders?page=${page}&size=${PER_PAGE}`);
      const data = res.data;
      const items = data.content || (Array.isArray(data) ? data : []);
      setOrders(items);
      setTotal(data.totalElements || items.length);
      setTotalPages(data.totalPages || 1);
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const filtered = orders.filter(o => {
    const matchSearch = !search || (o.orderNumber || '').toLowerCase().includes(search.toLowerCase()) ||
      (o.customerName || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'ALL' || o.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const res = await api.put(`/api/admin/orders/${orderId}/status`, { status: newStatus });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
      if (selectedOrder?.id === orderId) setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      toast.success(`Status updated to ${newStatus}`);
    } catch {
      toast.error('Failed to update order status');
    }
  };

  const statCounts = {
    all: orders.length,
    processing: orders.filter(o => o.status === 'PROCESSING').length,
    shipped: orders.filter(o => o.status === 'SHIPPED').length,
    delivered: orders.filter(o => o.status === 'DELIVERED').length,
    cancelled: orders.filter(o => o.status === 'CANCELLED').length,
  };

  return (
    <div>
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
        {[
          { label: 'Total Orders', value: statCounts.all, color: '#EFF6FF', emoji: '📦' },
          { label: 'Processing', value: statCounts.processing, color: '#FFF8E8', emoji: '⏳' },
          { label: 'Shipped', value: statCounts.shipped, color: '#EFF6FF', emoji: '🚚' },
          { label: 'Delivered', value: statCounts.delivered, color: '#E8F8F5', emoji: '✅' },
        ].map(card => (
          <div key={card.label} className="stat-card">
            <div className="stat-icon" style={{ background: card.color }}>{card.emoji}</div>
            <div className="stat-info"><p className="stat-label">{card.label}</p><p className="stat-value">{card.value}</p></div>
          </div>
        ))}
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <h3>Orders <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 400 }}>({total})</span></h3>
          <div className="admin-card-actions">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {['ALL', ...ALL_STATUSES].map(s => (
                <button key={s} onClick={() => setStatusFilter(s)} style={{
                  padding: '5px 12px', borderRadius: 20, border: '1px solid', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  background: statusFilter === s ? 'var(--huge-purple)' : 'var(--bg-card)',
                  borderColor: statusFilter === s ? 'var(--huge-purple)' : 'var(--border)',
                  color: statusFilter === s ? 'white' : 'var(--text-secondary)',
                }}>{s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}</button>
              ))}
            </div>
            <div className="admin-search">
              <FiSearch />
              <input placeholder="Search order ID, customer..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <button onClick={fetchOrders} style={{ padding: '8px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
              <FiRefreshCw size={15} />
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #E5E7EB', borderTopColor: '#7C3AED', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
            Loading orders...
          </div>
        ) : (
          <>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr><th>Order ID</th><th>Customer</th><th>Date</th><th>Items</th><th>Amount</th><th>Payment</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                      {orders.length === 0 ? 'No orders placed yet' : 'No orders match the current filter'}
                    </td></tr>
                  ) : filtered.map(order => (
                    <tr key={order.id}>
                      <td style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 12 }}>#{order.orderNumber}</td>
                      <td>
                        <p style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{order.customerName || 'Unknown'}</p>
                        <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{order.customerEmail}</p>
                      </td>
                      <td style={{ fontSize: 13 }}>{fmtDate(order.createdAt)}</td>
                      <td>{order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? 's' : ''}</td>
                      <td style={{ fontWeight: 700 }}>₹{Number(order.totalAmount || 0).toLocaleString()}</td>
                      <td><span className="table-badge badge-gray">{order.paymentMethod}</span></td>
                      <td>
                        <select value={order.status} onChange={e => handleStatusChange(order.id, e.target.value)}
                          className="admin-select" style={{ padding: '5px 8px', fontSize: 12 }}>
                          {ALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td>
                        <button className="table-action-btn view" onClick={() => setSelectedOrder(order)}><FiEye /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="admin-pagination">
                <p>Page {page + 1} of {totalPages} &nbsp;·&nbsp; {total} total orders</p>
                <div className="admin-pagination-btns">
                  <button className="page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const pg = totalPages <= 5 ? i : Math.min(Math.max(page - 2, 0), totalPages - 5) + i;
                    return <button key={pg} className={`page-btn${pg === page ? ' active' : ''}`} onClick={() => setPage(pg)}>{pg + 1}</button>;
                  })}
                  <button className="page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next ›</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="admin-modal-overlay" onClick={e => e.target === e.currentTarget && setSelectedOrder(null)}>
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3>Order #{selectedOrder.orderNumber}</h3>
              <button className="modal-close-btn" onClick={() => setSelectedOrder(null)}><FiX /></button>
            </div>
            <div className="admin-modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                <div>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>CUSTOMER</p>
                  <p style={{ fontWeight: 700 }}>{selectedOrder.customerName || 'Unknown'}</p>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{selectedOrder.customerEmail}</p>
                </div>
                <div>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>ORDER DATE</p>
                  <p style={{ fontWeight: 700 }}>{fmtDate(selectedOrder.createdAt)}</p>
                </div>
                <div>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>PAYMENT</p>
                  <p style={{ fontWeight: 700 }}>{selectedOrder.paymentMethod}</p>
                </div>
                <div>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>TOTAL AMOUNT</p>
                  <p style={{ fontWeight: 700, color: '#10B981', fontSize: 20 }}>₹{Number(selectedOrder.totalAmount || 0).toLocaleString()}</p>
                </div>
              </div>

              {selectedOrder.address && (
                <div style={{ marginBottom: 16 }}>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>DELIVERY ADDRESS</p>
                  <p style={{ fontWeight: 600 }}>
                    {selectedOrder.address.name} · {selectedOrder.address.phone}<br />
                    {selectedOrder.address.addressLine}, {selectedOrder.address.city}, {selectedOrder.address.state} - {selectedOrder.address.pincode}
                  </p>
                </div>
              )}

              {selectedOrder.items?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>ORDER ITEMS</p>
                  {selectedOrder.items.map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ fontSize: 24 }}>{item.imageEmoji || '👗'}</span>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontWeight: 600, fontSize: 14 }}>{item.productName}</p>
                        <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{item.productBrand} · {item.selectedSize} · {item.selectedColor}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontWeight: 700 }}>₹{Number(item.price).toLocaleString()}</p>
                        <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Qty: {item.quantity}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid var(--border)' }} />
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>UPDATE STATUS</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {ALL_STATUSES.map(s => (
                  <button key={s} onClick={() => handleStatusChange(selectedOrder.id, s)} style={{
                    padding: '8px 16px', borderRadius: 4, border: `2px solid ${selectedOrder.status === s ? 'var(--huge-purple)' : 'var(--border)'}`,
                    background: selectedOrder.status === s ? 'var(--huge-purple)' : 'var(--bg-card)',
                    color: selectedOrder.status === s ? 'white' : 'var(--text-secondary)', fontSize: 12, fontWeight: 700, cursor: 'pointer'
                  }}>{s}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
